class ProductFlexiblePlanSingle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
		this.element = this;
		this.masterSelector = this.element.querySelector('#product-flexible-plan-js');
    this.selector = {
			plan: '.product-plan',
			planOption: '.product-plan-option',
			planName: '.product-plan-name',
			sellingPlan: '#selling-plan',
			add: '.btn-plan-add'
    }

    this.init();
  }

  init() {
    // Remember the subscription button's original label so it can be restored when
    // switching back from a bundle (non-subscription) option.
    const subscribeBtn = this.element.querySelector('.btn-plan-add[data-type="subscription"]');
    this.subscribeLabel = subscribeBtn ? subscribeBtn.textContent.trim() : '';

    // On subscription-priced templates, the default plan shows the subscription price
    // (with the recurring discount) instead of the one-time price.
    this.subPriceEnabled = this.dataset.subPrice === 'true';

    this.initEventListener();
		this.initAddToCart();
		this.initPackChange();
    this.initNappySize();
    this.initPrice();
    this.syncMatchOptions();
    this.syncPlanToVariant();
    this.syncSubscriptionUI();
    this.syncPlanNote();
    this.syncPlanTitle();
    this.syncPlanDesc();
    this.syncPlanMedia();

		document.addEventListener('on:variant:change', (event) => {
			this.initNappySize();
			this.initPackChange();
      this.initPrice();
      this.syncMatchOptions();
      this.syncPlanToVariant();
      this.syncSubscriptionUI();
		});

		this.element.querySelector(this.selector.sellingPlan)?.addEventListener('change', () => {
			this.updateBoxInfo();
			// Reflect the selected frequency's subscription price in the plan header
			if (this.subPriceEnabled) this.initPrice();
			// Linked cards (e.g. the Deluxe Bundle) price off their own selling plans,
			// so they have to be re-priced for the new frequency too.
			this.syncMatchOptions();
			// The frequency selector may include a "One-time purchase" entry — keep the
			// button label and commitment notice in step with it.
			this.syncSubscriptionUI();
		});

		this.initChevron();
  }

	initChevron() {
		const select = this.element.querySelector(this.selector.sellingPlan);
		const container = select?.closest('.recharge-frequency');

		if (!select || !container) return;

		// Native selects don't emit open/close events, so approximate the open
		// state: clicking toggles it, and blur/change/outside-click resets it.
		select.addEventListener('mousedown', () => {
			container.classList.toggle('is-open');
		});

		select.addEventListener('keydown', (event) => {
			if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
				container.classList.add('is-open');
			}
		});

		select.addEventListener('blur', () => {
			container.classList.remove('is-open');
		});

		select.addEventListener('change', () => {
			container.classList.remove('is-open');
		});

		// blur doesn't reliably fire when dismissing an open native select by
		// clicking elsewhere on the page, so reset on any outside pointer down.
		document.addEventListener('mousedown', (event) => {
			if (!container.contains(event.target)) {
				container.classList.remove('is-open');
			}
		});
	}

  initEventListener() {
		// Plan switch event
		this.element.querySelectorAll(this.selector.plan).forEach((plan) => {
			plan.addEventListener('click', () => {
				this.element.querySelector(`${this.selector.plan}.active`)?.classList.remove('active');
				plan.classList.toggle('active');
			})
		})

		// Option titles link out to their product page — don't treat that as a card select
		this.element.querySelectorAll('.js-plan-title-link').forEach((link) => {
			link.addEventListener('click', (event) => {
				event.stopPropagation();
				this.showNavigatingSpinner(link.closest(this.selector.planOption), event);
			});
		});

		// A restored back/forward-cache page would otherwise keep showing the spinner
		window.addEventListener('pageshow', () => {
			this.element.querySelectorAll(`${this.selector.planOption}.is-navigating`).forEach((option) => {
				option.classList.remove('is-navigating');
			});
		});

		// Plan option switch event — one active card per plan row, so the one-time
		// row's selection is independent of the subscription row's.
		this.element.querySelectorAll(this.selector.planOption).forEach((option) => {
			option.addEventListener('click', (event) => {
				// Link-out cards (bundles needing a variant choice on their own page)
				// navigate via their anchor — never become the selected option. Clicks
				// landing on the card rim outside the anchor navigate programmatically,
				// so the spinner never shows without a navigation following it.
				if (option.dataset.type === 'linkout') {
					if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
					const anchor = option.querySelector(':scope > a');
					this.showNavigatingSpinner(option, event);
					if (anchor && !anchor.contains(event.target)) {
						window.location.href = anchor.href;
					}
					return;
				}
				const planRow = option.closest(this.selector.plan);
				planRow.querySelector(`${this.selector.planOption}.active`)?.classList.remove('active');
				option.classList.toggle('active');

				this.mirrorOptionPrice(option);

				// Update plan header nappy count and box info for the selected option (honours data-box-count, e.g. Deluxe = 7 boxes)
				this.updatePlanName(option.closest('.product-plan'));
				this.updateBoxInfo();
				// Bundle options can't be subscribed — reflect that in the subscription UI
				this.syncSubscriptionUI();
				// Selecting a plan-less card hides the frequency selector, which changes
				// whether any card may show subscription pricing — re-price them all.
				// Must run after syncSubscriptionUI, which sets that visibility.
				if (this.subPriceEnabled) this.initPrice(true);
				this.syncMatchOptions();
				// Prices may have just changed, so re-mirror: the earlier call copied the
				// figures this card had before it was re-priced.
				this.mirrorOptionPrice(option);
				this.syncPlanNote();
				this.syncPlanTitle();
				this.syncPlanDesc();
				this.syncPlanMedia();
			})
		})
  }

	// Rebuild a card's price block — price, strikethrough and save badge together.
	// Rewriting the whole block is what keeps them in step: updating the price text
	// alone leaves the previous variant's compare price and badge in place, which
	// reads as a strikethrough below the actual price.
	renderCardPrice(card, price, compareAt, showSaveBadge) {
		const wrapper = card?.querySelector('.product-plan-option-wrapper');
		if (!wrapper) return;

		let comparePriceCnt = '';
		let savingBadgeCnt = '';
		if (compareAt > price) {
			comparePriceCnt = `<span class="compare-price">${this.formatMoney(compareAt)}</span>`;
			if (showSaveBadge) {
				const saveAmount = compareAt - price;
				savingBadgeCnt = `<div class="plan-option-save-badge">Save ${Math.round(saveAmount * 100 / compareAt)}%</div>`;
			}
		}
		savingBadgeCnt = this.buildBadgeHtml(card, savingBadgeCnt);

		card.querySelector('.plan-option-price')?.remove();
		card.querySelector('.plan-option-save-badge')?.remove();

		// Keep price/badge above the variant description (e.g. "6 boxes" / "5 packs")
		const descEl = wrapper.querySelector('.plan-option-desc');
		const priceHtml = `<div class="plan-option-price"><span class="regular-price">${this.formatMoney(price)}</span>${comparePriceCnt}</div>`;
		if (descEl) {
			descEl.insertAdjacentHTML('beforebegin', priceHtml);
			if (savingBadgeCnt) descEl.insertAdjacentHTML('beforebegin', savingBadgeCnt);
		} else {
			wrapper.insertAdjacentHTML('beforeend', priceHtml);
			if (savingBadgeCnt) wrapper.insertAdjacentHTML('beforeend', savingBadgeCnt);
		}
	}

	// Select a row's own base-product card (the one that adds this product rather
	// than a linked bundle), used when a bundle card can no longer be honoured.
	selectBaseOption(planRow) {
		const baseCard = planRow?.querySelector(
			`${this.selector.planOption}[data-type="default"], ${this.selector.planOption}[data-type="onetime-default"]`
		);
		if (baseCard && !baseCard.classList.contains('active')) baseCard.click();
	}

	// Copy an option card's price into its plan row header.
	mirrorOptionPrice(option) {
		const planRow = option.closest(this.selector.plan);
		if (!planRow) return;
		const elComparePrice = planRow.querySelector('.product-plan-price .compare-price');
		const elRegularPrice = planRow.querySelector('.product-plan-price .regular-price');
		const optComparePrice = option.querySelector('.compare-price');
		const optRegularPrice = option.querySelector('.regular-price');
		if (elRegularPrice && optRegularPrice) {
			elRegularPrice.innerText = optRegularPrice.innerText || '';
		}
		// Mirror the active option's compare price, hiding it when that option has none
		if (elComparePrice) {
			if (optComparePrice && optComparePrice.innerText.trim()) {
				elComparePrice.innerText = optComparePrice.innerText;
				elComparePrice.classList.remove('hidden');
			} else {
				elComparePrice.innerText = '';
				elComparePrice.classList.add('hidden');
			}
		}
	}

	initNappySize() {
		this.element.querySelectorAll(this.selector.plan).forEach((plan) => {
			this.updatePlanName(plan);
		})
		this.updateBoxInfo();
	}

	updatePlanName(plan) {
		const elName = plan.querySelector(this.selector.planName);
		if (!elName) return;
		const defaultTitle = elName.dataset.prefix;
		const unitLabel = this.dataset.unitLabel || 'wipes';

		// Package PDPs title the plan with the selected package ("Starter Bundle")
		// instead of a fixed label. Only the subscription row — the one-time row
		// keeps its own "One-time purchase" heading.
		const subscribeBtn = this.element.querySelector('.btn-plan-add[data-type="subscription"]');
		const isSubscriptionRow = subscribeBtn ? subscribeBtn.closest(this.selector.plan) === plan : true;
		if (this.dataset.planNameFromOption === 'true' && isSubscriptionRow) {
			const selectedCard = plan.querySelector(`${this.selector.planOption}.active`);
			const optionTitle = selectedCard?.dataset.optionTitle;
			if (optionTitle) {
				elName.innerText = optionTitle;
				return;
			}
		}

		// A selected bundle card contains a different mix of packs to the host
		// product, so its total comes from the card's own contents list rather
		// than the selected variant. Only valid where the header counts wipes —
		// the contents table measures wipes, so a nappy-counting header (starter
		// PDPs) must keep using the variant's own count.
		const activeOption = plan.querySelector(`${this.selector.planOption}.active`);
		if (activeOption && activeOption.dataset.type === 'bundle' && unitLabel === 'wipes') {
			const bundleCount = this.getOptionUnitCount(activeOption);
			if (bundleCount > 0) {
				elName.innerText = defaultTitle ? `${defaultTitle} (${bundleCount} ${unitLabel})` : `${bundleCount} ${unitLabel}`;
				return;
			}
		}

    const variant = this.getSelectedVariant();

    const option = variant ? this.masterSelector.querySelector(`option[value="${variant.id}"]`) : null;
    if(!option) return;

    const count = parseFloat(option.dataset.packSize) * parseFloat(option.dataset.nappySizePerPack);
		// Subscription plans show "Flexible Plan (N wipes)"; non-subscription products have a
		// blank prefix and show just "N wipes" without parentheses. A missing per-pack
		// metafield yields 0/NaN — show only the prefix rather than "NaN wipes".
		if (!count || Number.isNaN(count)) {
			elName.innerText = defaultTitle || elName.dataset.emptyTitle || '';
			return;
		}
		elName.innerText = defaultTitle ? `${defaultTitle} (${count} ${unitLabel})` : `${count} ${unitLabel}`;
	}

  // skipReselect: caller is already inside the option-click handler, where the
  // trailing activeOption.click() would recurse infinitely.
  initPrice(skipReselect) {
    const variant = this.getSelectedVariant();

    // Keep the one-time purchase plan's price in sync with the selected variant —
    // but not while a bundle card is selected in that row, whose price belongs to
    // the linked product and doesn't move with this product's variant.
    if (variant) {
      const onetimeBtn = this.element.querySelector('.btn-plan-add[data-type="onetime"]');
      const onetimeRow = onetimeBtn?.closest(this.selector.plan);
      const onetimeActive = onetimeRow?.querySelector(`${this.selector.planOption}.active`);
      const onetimePriceEl = onetimeRow?.querySelector('.product-plan-price .regular-price');
      const showsBaseProduct = !onetimeActive || onetimeActive.dataset.type === 'onetime-default';
      if (onetimePriceEl && showsBaseProduct) onetimePriceEl.innerText = this.formatMoney(variant.price);
    }

    const planOption = this.element.querySelector(`${this.selector.planOption}[data-type="default"]`);
    if(!planOption) return;
    // Without product data there is nothing to re-price with — leave the prices
    // Liquid rendered and fall through to the selection below, which the rest of
    // the widget (plan header, note, description, gallery) hangs off.
    if (!variant) {
      if (!skipReselect) this.reselectActiveCard(planOption);
      return;
    }
    // Prefer the live compare-at price; fall back to the block's manual compare price when enabled
    let displayPrice = variant.price;
    let compareAt = variant.compare_at_price;
    const fallbackCompare = parseFloat(planOption.dataset.fallbackCompare);
    if (!(compareAt > variant.price) && fallbackCompare > variant.price) compareAt = fallbackCompare;
    // On subscription-priced templates the default option is a subscription: show the
    // selling-plan price as the main price and the one-time price as the strikethrough.
    // The strikethrough conveys the saving, so no extra "Save X%" badge is added.
    let showSaveBadge = true;
    if (this.subPriceEnabled) {
      const alloc = this.getSubscriptionAllocation(variant);
      if (alloc) {
        displayPrice = alloc.price;
        compareAt = alloc.compare_at_price;
        showSaveBadge = false;
      }
    }
    this.renderCardPrice(planOption, displayPrice, compareAt, showSaveBadge);
    // The per-pack figure for the selected size drives every card's count, so
    // derive it once here — syncMatchOptions() reuses it for the bundle cards.
    this.hostVariantTitle = variant.title;
    this.perPack = this.resolvePerPack(variant, planOption);

    // Reflect the selected variant title in the first option's description
    const variantTitleEl = planOption.querySelector('.js-plan-option-variant');
    this.setVariantLine(variantTitleEl, variant.title, planOption.dataset.packs, this.perPack);

    // Keep the base-product card in the one-time row (when rendered) on the
    // selected variant's one-time price and title.
    const onetimeCard = this.element.querySelector(`${this.selector.planOption}[data-type="onetime-default"]`);
    if (onetimeCard && variant) {
      // Always the plain variant price here — never a selling-plan price.
      let cardCompare = variant.compare_at_price;
      const cardFallback = parseFloat(onetimeCard.dataset.fallbackCompare);
      if (!(cardCompare > variant.price) && cardFallback > variant.price) cardCompare = cardFallback;
      this.renderCardPrice(onetimeCard, variant.price, cardCompare, true);
      const cardTitleEl = onetimeCard.querySelector('.js-plan-option-variant');
      this.setVariantLine(cardTitleEl, variant.title, onetimeCard.dataset.packs, this.perPack);
    }

    if (!skipReselect) this.reselectActiveCard(planOption);
  }

  // Re-apply the selection so the header follows it — the shopper's chosen card
  // must survive a variant change, so only fall back to the default card when its
  // row has nothing selected.
  reselectActiveCard(planOption) {
    const planRow = planOption.closest(this.selector.plan);
    (planRow?.querySelector(`${this.selector.planOption}.active`) || planOption).click();
  }

  // Starter products store variant titles in caps ("SIZE 1 (0-5KG) 52 NAPPIES").
  // Split into the three display lines used on the cards — size, count, per-pack —
  // in sentence case. Returns null for titles not matching that shape.
  parseVariantLine(title, packs, perPack) {
    if (!title || !title.includes('(')) return null;
    const down = title.toLowerCase();
    // Titles come in both cases: "SIZE 1 (0-5KG) 52 NAPPIES" (starter packs) and
    // "Size 1 Nappies (0-5kg)" (Deluxe Bundle, which carries no total).
    if (!down.startsWith('size ')) return null;
    const sizeNum = (down.split('size')[1] || '').trim().split(' ')[0];
    const range = (down.split('(')[1] || '').split(')')[0];
    const after = (down.split(')')[1] || '').trim();
    if (!sizeNum || !range) return null;

    // A shared per-pack figure wins over the title's own total, so each card's
    // count reflects its package size against the selected nappy size.
    const packCount = parseFloat(packs);
    const sharedPerPack = parseFloat(perPack);
    let total;
    let perPackOut = 0;
    if (sharedPerPack > 0 && packCount > 0) {
      total = sharedPerPack * packCount;
      perPackOut = sharedPerPack;
    } else {
      total = parseInt(after, 10);
      if (packCount > 0 && total > 0) perPackOut = Math.round(total / packCount);
    }

    const size = `Size ${sizeNum} (${range})`;
    if (isNaN(total) || total <= 0) return { size, count: '', perPack: '' };

    // Read the unit from the whole title — on Deluxe titles the words sit before
    // the parens ("Size 5 Nappy Pants (13-18kg)"), leaving nothing after them.
    const unit = down.includes('pant') ? 'pants' : 'nappies';
    return {
      size,
      count: `${total} ${unit}`,
      perPack: perPackOut > 0 ? `(${perPackOut} per pack)` : ''
    };
  }

  // Nappies per pack for the selected size, from the host variant's own total
  // divided by the pack count of the card that adds this product. Returns 0 when
  // the title carries no total (non-starter products), so callers fall back to
  // each card's own variant title.
  derivePerPack(title, packs) {
    const packCount = parseFloat(packs);
    if (!title || !title.toLowerCase().startsWith('size ') || !(packCount > 0)) return 0;
    const after = (title.toLowerCase().split(')')[1] || '').trim();
    const total = parseInt(after, 10);
    if (isNaN(total) || total <= 0) return 0;
    return Math.round(total / packCount);
  }

  // The per-pack figure for the selected variant: from this product's own title
  // where it carries a total, otherwise from a bundle card whose size-matched
  // variant does (the Deluxe Bundle's titles carry no count of their own).
  resolvePerPack(variant, defaultCard) {
    const own = this.derivePerPack(variant.title, defaultCard?.dataset.packs);
    if (own > 0) return own;

    const cards = this.element.querySelectorAll(`${this.selector.planOption}[data-type="bundle"]`);
    for (const card of cards) {
      const mapEl = card.querySelector('script.js-variant-match-map');
      if (!mapEl) continue;
      let entry = null;
      try {
        entry = JSON.parse(mapEl.textContent)[variant.id];
      } catch (e) {
        continue;
      }
      const perPack = entry ? this.derivePerPack(entry.title, card.dataset.packs) : 0;
      if (perPack > 0) return perPack;
    }
    return 0;
  }

  // Render a variant line into a card as three stacked lines, matching the markup
  // in product-plan-variant-line.liquid: size, the bolded count, then the per-pack
  // figure. Titles that don't parse render as-is on a single line.
  setVariantLine(el, title, packs, perPack) {
    if (!el) return;
    const parts = this.parseVariantLine(title, packs, perPack);
    if (!parts) {
      el.textContent = title == null ? '' : title;
      return;
    }
    const line = (text) => `<span class="plan-option-variant-line">${this.escapeHtml(text)}</span>`;
    let html = line(parts.size);
    if (parts.count) html += `<span class="plan-option-variant-line"><strong>${this.escapeHtml(parts.count)}</strong></span>`;
    if (parts.perPack) html += line(parts.perPack);
    el.innerHTML = html;
  }

  escapeHtml(value) {
    const holder = document.createElement('div');
    holder.textContent = value == null ? '' : value;
    return holder.innerHTML;
  }

	// Auto-select the matching product plan for the chosen variant: a single (1) pack maps to the
	// one-time purchase plan; any multi-pack maps back to the flexible (subscription) plan.
	syncPlanToVariant() {
		const onetimeBtn = this.element.querySelector('.btn-plan-add[data-type="onetime"]');
		if (!onetimeBtn) return; // no one-time plan rendered → nothing to switch between

		// If no variant is a multi-pack (e.g. every title parses to pack size 1, as on the
		// starter templates), pack size can't drive plan switching — leave plans as-is.
		const hasMultiPack = [...this.masterSelector.querySelectorAll('option')]
			.some((o) => parseFloat(o.dataset.packSize) > 1);
		if (!hasMultiPack) return;

		const variant = this.getSelectedVariant();
		if (!variant) return;

		const masterOpt = this.masterSelector.querySelector(`option[value="${variant.id}"]`);
		const packSize = masterOpt ? parseFloat(masterOpt.dataset.packSize) : NaN;
		const isSinglePack = packSize === 1;

		const onetimePlan = onetimeBtn.closest('.product-plan');
		const flexibleBtn = this.element.querySelector('.btn-plan-add[data-type="subscription"]');
		const flexiblePlan = flexibleBtn ? flexibleBtn.closest('.product-plan') : this.element.querySelector(this.selector.plan);

		// A single (1) pack can't be subscribed to — hide the flexible plan entirely so only the
		// one-time purchase is available; restore it for any multi-pack selection.
		if (flexiblePlan) flexiblePlan.classList.toggle('hidden', isSinglePack);

		const target = isSinglePack ? onetimePlan : flexiblePlan;
		if (!target || target.classList.contains('active')) return;

		this.element.querySelector(`${this.selector.plan}.active`)?.classList.remove('active');
		target.classList.add('active');
	}

	// The frequency selector and commitment notice only apply when the active card can
	// actually be subscribed: the default card when the base product has selling plans,
	// or a bundle card whose linked product has its own plans (frequency-mapped). The
	// selector may also carry a "One-time purchase" entry — when that's chosen, the
	// button label and notice drop back to their one-time state.
	syncSubscriptionUI() {
		const subscribeBtn = this.element.querySelector('.btn-plan-add[data-type="subscription"]');
		if (!subscribeBtn) return;
		const planRow = subscribeBtn.closest(this.selector.plan);
		const activeOption = planRow.querySelector(`${this.selector.planOption}.active`);
		if (!activeOption) return;

		const action = subscribeBtn.closest('.product-plan-action');
		const frequency = action?.querySelector('.recharge-frequency');
		const notice = action?.querySelector('.subscription-commitment-notice');
		const sellingPlanEl = this.element.querySelector(this.selector.sellingPlan);

		let plansAvailable = false;
		if (sellingPlanEl) {
			if (activeOption.dataset.type === 'default') {
				plansAvailable = sellingPlanEl.dataset.basePlans === 'true';
			} else {
				const planMap = this.getOptionPlanMap(activeOption);
				plansAvailable = !!planMap && Object.keys(planMap).length > 0;
			}
		}

		frequency?.classList.toggle('hidden', !plansAvailable);
		const subscribing = plansAvailable && sellingPlanEl && sellingPlanEl.value !== '';
		notice?.classList.toggle('hidden', !subscribing);

		const nonSubLabel = subscribeBtn.dataset.labelNonsub || 'Add to Cart';
		subscribeBtn.textContent = subscribing ? (this.subscribeLabel || nonSubLabel) : nonSubLabel;
	}

	// Overlay a loading spinner on a tile that is navigating to another product page.
	// Skipped for modified clicks (cmd/ctrl/shift open a new tab — this page stays).
	showNavigatingSpinner(option, event) {
		if (!option) return;
		if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)) return;
		option.classList.add('is-navigating');
	}

	// Wipes contained in one pack of each product a bundle can be made up of.
	// Matched against a bundle card's contents lines, most specific first —
	// "travel size Water Wipes" must not be counted as a full-size pack.
	// Lines that match nothing (nappies, nappy bags) contribute no wipes.
	static get WIPES_PER_PACK() {
		return [
			{ pattern: /travel/, wipes: 24 },
			{ pattern: /dry\s*wipes/, wipes: 70 },
			{ pattern: /antibacterial/, wipes: 10 },
			{ pattern: /water\s*wipes/, wipes: 64 },
		];
	}

	// Total the wipes advertised by a bundle card by reading its contents lines
	// ("4 × Packs Water Wipes" → 4 × 64). Returns 0 when the card has no
	// countable contents, so the caller can fall back to the variant's own count.
	getOptionUnitCount(option) {
		const desc = option?.querySelector('.plan-option-desc');
		if (!desc) return 0;

		let total = 0;
		desc.querySelectorAll('.paragraph').forEach((line) => {
			// The variant line describes the host product, not the bundle contents
			if (line.querySelector('.js-plan-option-variant')) return;

			const text = line.textContent.trim().toLowerCase();
			const packs = parseInt((text.match(/^(\d+)\s*[×x]/) || [])[1], 10);
			if (!packs) return;

			const rule = ProductFlexiblePlanSingle.WIPES_PER_PACK.find((r) => r.pattern.test(text));
			if (rule) total += packs * rule.wipes;
		});

		return total;
	}

	getOptionPlanMap(option) {
		if (!option || !option.dataset.planMap) return null;
		try {
			return JSON.parse(option.dataset.planMap);
		} catch {
			// Malformed map — treat the option as having no subscription plans
			return null;
		}
	}

	updateBoxInfo() {
		const infoEl = this.element.querySelector('.product-plan-info');
		if(!infoEl) return;
		const sizeEl = document.querySelector('.js-variant-option[data-type="size"]:checked');
		if(!sizeEl) return;
		const target = this.masterSelector.querySelector(`option[data-pack="8 packs"][data-size="${sizeEl.value}"]`);
		if(!target) return;
		const nappySizePerPack = parseFloat(target.dataset.nappySizePerPack);
		if(isNaN(nappySizePerPack)) return;
		const subscribeBtn = this.element.querySelector('.btn-plan-add[data-type="subscription"]');
		const activeOption = subscribeBtn
			? subscribeBtn.closest(this.selector.plan).querySelector(`${this.selector.planOption}.active`)
			: this.element.querySelector(`${this.selector.planOption}.active`);
		const boxCount = activeOption ? parseFloat(activeOption.dataset.boxCount) : NaN;
		const boxes = isNaN(boxCount) ? 8 : boxCount;
		const sellingPlan = this.element.querySelector(this.selector.sellingPlan);
		let deliveryDays = 30;
		if(sellingPlan && sellingPlan.selectedIndex >= 0) {
			const frequency = parseFloat(sellingPlan.options[sellingPlan.selectedIndex].dataset.frequency);
			if(!isNaN(frequency) && frequency > 0) {
				deliveryDays = frequency;
			}
		}
		const nappiesPerBox = boxes * nappySizePerPack;
		const changesPerDay = Math.round(nappiesPerBox / deliveryDays);
		infoEl.innerHTML = `${nappiesPerBox} nappies per box • ${changesPerDay} changes/day`;
	}

	initPackChange() {
		const packNightPromo = document.querySelector('.pack-night-promo');
		if(!packNightPromo) {
			return;
		}
		const selectedPack = document.querySelector(`.js-variant-option[data-type="pack"]:checked`);
		if(selectedPack && selectedPack.value == '3 packs') {
			packNightPromo.classList.remove('hidden');
		}
		else {
			packNightPromo.classList.add('hidden');
		}
	}

	// Writes the selected package's wipes sentence into the note above "Choose Your
	// Plan". The note sits outside this element but inside the same .js-product
	// wrapper, so the lookup is scoped to that wrapper — never the document. The
	// quick-add drawer injects a whole .js-product (widget included) into the page,
	// and a document-wide query here would let the drawer's widget blank and hide
	// the underlying PDP's note.
	syncPlanNote() {
		const scope = this.closest('.js-product') || document;
		const target = scope.querySelector('[data-plan-note-target]');
		if (!target) return;

		const row = this.element.querySelector(`${this.selector.plan}.active`) || this.element;
		const active = row.querySelector(`${this.selector.planOption}.active`);
		// Mid-click there can briefly be no active card. Leave whatever is on screen
		// rather than blanking, so the note can never flash out.
		if (!active) return;

		const note = active.dataset.planNote || '';
		target.textContent = note;
		const panel = target.closest('.info-note') || target;
		panel.style.display = note ? '' : 'none';
	}

	// The card driving the page-level bits (note, description, gallery stand-in):
	// the active card of the active plan row.
	activePlanOption() {
		const row = this.element.querySelector(`${this.selector.plan}.active`) || this.element;
		return row.querySelector(`${this.selector.planOption}.active`);
	}

	// Swaps the copy above the widget for the selected package product's own Short
	// description metafield. Scoped like syncPlanNote — never document-wide, or the
	// quick-add drawer's widget would rewrite the description of the PDP behind
	// it.
	syncPlanDesc() {
		const scope = this.closest('.js-product') || document;
		const target = scope.querySelector('[data-plan-desc-target]');
		if (!target) return;

		const active = this.activePlanOption();
		// Mid-click there can briefly be no active card — leave what's on screen.
		if (!active) return;

		// The block's own copy — the current product's Short description, or its
		// merchant-authored fallback — stands in for a package with the metafield unset.
		if (this.planDescDefault === undefined) this.planDescDefault = target.innerHTML;

		const index = active.dataset.optIndex;
		const source = index
			? this.element.querySelector(`.js-plan-desc-source[data-opt-index="${index}"]`)
			: null;

		// Merchant-authored product HTML from Liquid, not user input.
		target.innerHTML = source ? source.innerHTML : this.planDescDefault;
	}

	// Retitles the page with the selected package's own product title — the real
	// product name, not the card's merchant-written label. Scoped like the note and
	// description, so a quick-add drawer can't retitle the PDP behind it.
	syncPlanTitle() {
		const scope = this.closest('.js-product') || document;
		const target = scope.querySelector('[data-plan-title-target]');
		if (!target) return;

		const active = this.activePlanOption();
		if (!active) return;

		// The heading as Liquid rendered it is the fallback for a card with no linked
		// product of its own.
		if (this.planTitleDefault === undefined) this.planTitleDefault = target.textContent;

		// textContent, not innerHTML — a product title is text, and the heading holds
		// nothing else worth preserving.
		target.textContent = active.dataset.productTitle || this.planTitleDefault;
	}

	// Stands the selected package's featured image in for the gallery. Only linked
	// packages have one, so selecting this product's own option restores the
	// gallery — which still holds its variant images and thumbs, having only been
	// hidden rather than rebuilt.
	syncPlanMedia() {
		const scope = this.closest('.js-product') || document;
		const swap = scope.querySelector('[data-plan-media-swap]');
		if (!swap) return;

		const active = this.activePlanOption();
		if (!active) return;

		const index = active.dataset.optIndex;
		const image = index
			? swap.querySelector(`.plan-media-swap__image[data-opt-index="${index}"]`)
			: null;

		const mediaWrap = swap.closest('.product-media');
		if (!mediaWrap) return;

		// Nothing on screen changes until the incoming image can paint: whatever the
		// column is showing — the gallery, or the previous package's stand-in — stays
		// put rather than blanking for the length of the download.
		const apply = () => {
			// The shopper may have picked another card while this image loaded — only
			// the card still selected may drive the gallery.
			if (this.activePlanOption()?.dataset.optIndex !== index) return;
			swap.querySelectorAll('.plan-media-swap__image').forEach((img) => {
				img.hidden = img !== image;
			});
			// Hiding the gallery mid-playback would leave a video running unseen.
			if (image && typeof window.pauseAllMedia === 'function') window.pauseAllMedia(mediaWrap);
			mediaWrap.classList.toggle('plan-media-swapped', Boolean(image));
		};

		if (image && !image.complete) {
			// The stand-ins sit in a display:none container, where a lazy image is
			// never eligible to load — waiting on one while it stays hidden would
			// deadlock. Eager loading is not tied to layout, so the fetch starts now.
			image.loading = 'eager';
			// A failed decode still swaps — the alt text is a better answer than a
			// gallery that silently ignores the selected package. The timeout covers
			// a decode that never settles; apply() is idempotent.
			image.decode().then(apply, apply);
			setTimeout(apply, 500);
			return;
		}
		apply();
	}

	initAddToCart() {
		this.element.querySelectorAll(this.selector.add).forEach((btn) => {
			btn.addEventListener('click', async () => {
				if (btn.getAttribute('aria-disabled') === 'true') return;
				const type = btn.dataset.type;

        const variant = this.getSelectedVariant();

				let data = null;
				if(type == 'subscription') {
					const planRow = btn.closest(this.selector.plan);
					const plan = planRow.querySelector(`${this.selector.planOption}.active`);
					const planType = plan ? plan.dataset.type : 'default';
					const sellingPlanEl = this.element.querySelector(this.selector.sellingPlan);
					const frequencyVisible = sellingPlanEl && !sellingPlanEl.closest('.recharge-frequency')?.classList.contains('hidden');
					if (planType == 'default') {
						data = {
							id: variant?.id,
							quantity: 1
						}
						// Subscription products render a #selling-plan frequency selector; when shown
						// and a frequency (not "One-time purchase") is chosen, add the chosen plan so
						// the default option is added as a subscription.
						if (frequencyVisible && sellingPlanEl.value) {
							data.selling_plan = sellingPlanEl.value;
						}
					}
					else {
						data = {
							id: plan.dataset.replaceProductVid ? plan.dataset.replaceProductVid : variant?.id,
							quantity: 1
						}
						// Bundle cards subscribe via their own product's plans: match the chosen
						// delivery frequency against the option's frequency → plan id map.
						const planMap = this.getOptionPlanMap(plan);
						if (planMap && frequencyVisible && sellingPlanEl.value) {
							const frequency = sellingPlanEl.options[sellingPlanEl.selectedIndex]?.dataset.frequency;
							if (frequency && planMap[frequency]) {
								data.selling_plan = planMap[frequency];
							}
						}
					}
				}
				else if (type == 'onetime') {
					// When the one-time row hosts package cards, the active card decides what is
					// added: the base variant or the bundle product's matched variant.
					const planRow = btn.closest(this.selector.plan);
					const activeCard = planRow.querySelector(`${this.selector.planOption}.active`);
					if (activeCard && activeCard.dataset.type === 'bundle' && activeCard.dataset.replaceProductVid) {
						data = {
							id: activeCard.dataset.replaceProductVid,
							quantity: 1
						}
					}
					else {
						data = {
							id: variant?.id,
							quantity: 1
						}
					}
				}
				else if (type == 'single') {
					selected_pack = '1 pack';
					masterOption = this.masterSelector.querySelector(`option[data-pack="${selected_pack}"][data-size="${selected_size}"]`);
					data = {
						id: masterOption.value,
						quantity: 1
					}
				}

				// No resolvable variant (a card with no linked variant, or a page carrying
				// no product data) — nothing to add, so don't put the button into its
				// loading state for a request that would fail.
				if (!data || !data.id) return;

				btn.setAttribute('aria-disabled', 'true');
				btn.classList.add('is-loading');

				try {
					await this.addToCartAndUpdateUI(data);
					btn.classList.add('is-success');
				} catch (err) {
					this.dispatchEvent(new CustomEvent('on:cart:error', {
						bubbles: true,
						detail: { error: err.message }
					}));
				} finally {
					btn.removeAttribute('aria-disabled');
					setTimeout(() => {
						btn.classList.remove('is-loading');
						btn.classList.remove('is-success');
					}, 1400);
				}
			})
		})
	}

	getCartDrawerInfo() {
		const cartDrawer = document.querySelector('cart-drawer');
		if (!cartDrawer) return null;
		const section = cartDrawer.closest('.shopify-section');
		if (!section) return null;
		return {
			cartDrawer,
			sectionId: section.id.replace('shopify-section-', '')
		};
	}

	getSectionsParam() {
		const info = this.getCartDrawerInfo();
		if (!info) return 'cart-icon-bubble';
		return `cart-icon-bubble,${info.sectionId}`;
	}

	updateCartIcon(response) {
		const cartIconBubble = document.getElementById('cart-icon-bubble');
		if (cartIconBubble && response.sections?.['cart-icon-bubble']) {
			cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
		}
	}

	async addToCartAndUpdateUI(lineItem) {
		if (!lineItem?.id) return;

		const cartInfo = this.getCartDrawerInfo();
		const response = await fetch(theme.routes.cartAdd, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json'
			},
			body: JSON.stringify({
				items: [lineItem],
				sections: this.getSectionsParam(),
				sections_url: window.location.pathname
			})
		});

		const addData = await response.json();
		const error = typeof addData.description === 'string' ? addData.description : addData.message;
		if (!response.ok) {
			throw new Error(error || response.status);
		}

		this.updateCartIcon(addData);

		const openDrawer = theme.settings.afterAtc === 'drawer';

		if (theme.settings.afterAtc === 'page') {
			setTimeout(() => {
				window.location.href = theme.routes.cart;
			}, 300);
			return addData;
		}

		if (cartInfo?.cartDrawer?.renderContents) {
			await cartInfo.cartDrawer.renderContents(addData, openDrawer);
		} else if (openDrawer) {
			document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
		}

		const cartResponse = await fetch(`${theme.routes.cart}.js`);
		if (cartResponse.ok) {
			const cart = await cartResponse.json();
			this.dispatchEvent(new CustomEvent('on:cart:add', {
				bubbles: true,
				detail: {
					cart,
					variantId: addData.variant_id || Number(lineItem.id)
				}
			}));
		}

		return addData;
	}

	// Keep every bundle card pointing at its linked product's size-matched variant:
	// update the add-to-cart variant id, the matched-variant description line, and —
	// unless the card advertises a manual teaser price — its price/compare/save badge.
	// Each card carries its own variant→match map in an embedded JSON script.
	syncMatchOptions() {
		const options = this.element.querySelectorAll(`${this.selector.planOption}[data-type="bundle"]`);
		if (!options.length) return;

		const variant = this.getSelectedVariant();
		if (!variant) return;

		options.forEach((option) => {
			const mapEl = option.querySelector('script.js-variant-match-map');
			if (!mapEl) return;
			let map = null;
			try {
				map = JSON.parse(mapEl.textContent);
			} catch {
				// Malformed map — leave the card on its Liquid-rendered variant
				return;
			}
			const entry = map[variant.id];
			if (!entry) {
				// An all-null map means the linked product isn't size-matched at all
				// (a single-variant bundle): its Liquid-rendered variant id stays
				// correct for every host variant, so the card is left as it is.
				// A map that matches *some* variants but not this one is different —
				// the card still points at the previously matched variant, so it must
				// not stay selected or Add to Cart would send the old size at the old
				// price. There, hand selection back to the row's own product card.
				const isSizeMatched = Object.values(map).some((value) => value);
				if (isSizeMatched && option.classList.contains('active')) {
					this.selectBaseOption(option.closest(this.selector.plan));
				}
				return;
			}

			option.dataset.replaceProductVid = entry.id;

			// With a shared per-pack figure the line comes from the host variant (the
			// linked variant's own title may carry no count, e.g. the Deluxe Bundle).
			const matchedTitleEl = option.querySelector('.js-plan-option-variant');
			const lineTitle = this.perPack > 0 ? this.hostVariantTitle : entry.title;
			if (lineTitle) this.setVariantLine(matchedTitleEl, lineTitle, option.dataset.packs, this.perPack);

			// A manual teaser price (e.g. an after-discount-code price) stays as rendered
			if (option.dataset.manualPrice === 'true') return;

			const wrapper = option.querySelector('.product-plan-option-wrapper');
			if (!wrapper) return;
			// A linked product with its own selling plans (the Deluxe Bundle) must show
			// the recurring price and its saving while a frequency is chosen — otherwise
			// the card advertises the one-time price for a subscription purchase.
			const subAlloc = this.getMatchedSubscriptionAllocation(option, entry);
			let price = subAlloc ? subAlloc.price : entry.price;
			let compareAt = subAlloc ? subAlloc.compare_at_price : entry.compare_at_price;
			const fallbackCompare = parseFloat(option.dataset.fallbackCompare);
			if (!(compareAt > price) && fallbackCompare > price) compareAt = fallbackCompare;
			const priceCnt = `<span class="regular-price">${this.formatMoney(price)}</span>`;
			let comparePriceCnt = '';
			let savingBadgeCnt = '';
			if (compareAt > price) {
				comparePriceCnt = `<span class="compare-price">${this.formatMoney(compareAt)}</span>`;
				const saveAmount = compareAt - price;
				savingBadgeCnt = `<div class="plan-option-save-badge">Save ${Math.round(saveAmount * 100 / compareAt)}%</div>`;
			}
			savingBadgeCnt = this.buildBadgeHtml(option, savingBadgeCnt);
			option.querySelector('.plan-option-price')?.remove();
			option.querySelector('.plan-option-save-badge')?.remove();
			const descEl = wrapper.querySelector('.plan-option-desc');
			const priceHtml = `<div class="plan-option-price">${priceCnt}${comparePriceCnt}</div>`;
			if (descEl) {
				descEl.insertAdjacentHTML('beforebegin', priceHtml);
				if (savingBadgeCnt) descEl.insertAdjacentHTML('beforebegin', savingBadgeCnt);
			} else {
				wrapper.insertAdjacentHTML('beforeend', priceHtml);
				if (savingBadgeCnt) wrapper.insertAdjacentHTML('beforeend', savingBadgeCnt);
			}

			// This runs after initPrice(), so a selected bundle card's freshly matched
			// price has to be pushed to the header or it would show the previous size's.
			if (option.classList.contains('active')) this.mirrorOptionPrice(option);
		});
	}

  // Returns the selling-plan allocation matching the chosen delivery frequency (or the first
  // allocation) for a variant, or null when the variant has no subscription pricing.
  // Subscription pricing for a linked (non-host) card at the chosen frequency.
  // The card's data-plan-map turns the selected frequency into the LINKED product's
  // selling plan id — the host's plan ids are different and would never match — and
  // the map entry carries that plan's allocation prices. Null when one-time is
  // selected, when the card has no plans, or when this frequency has no counterpart.
  getMatchedSubscriptionAllocation(option, entry) {
    if (!entry || !entry.plans) return null;
    const sellingPlanEl = this.element.querySelector(this.selector.sellingPlan);
    if (!sellingPlanEl || sellingPlanEl.value === '') return null;
    // A hidden frequency selector means the active card can't be subscribed, so no
    // card may advertise a subscription price — the shopper would have no control on
    // screen explaining it. Mirrors the frequencyVisible guard used by add-to-cart.
    if (sellingPlanEl.closest('.recharge-frequency')?.classList.contains('hidden')) return null;
    const planMap = this.getOptionPlanMap(option);
    if (!planMap) return null;
    const frequency = sellingPlanEl.options[sellingPlanEl.selectedIndex]?.dataset.frequency;
    if (!frequency) return null;
    const planId = planMap[frequency];
    if (!planId) return null;
    return entry.plans[planId] || null;
  }

  getSubscriptionAllocation(variant) {
    if (!variant || !Array.isArray(variant.selling_plan_allocations) || !variant.selling_plan_allocations.length) return null;
    const sellingPlanEl = this.element.querySelector(this.selector.sellingPlan);
    // "One-time purchase" is a real, blank-valued entry in the frequency selector
    // (enable_onetime_frequency). Picking it means no selling plan applies, so the
    // shopper must see the plain variant price — not a subscription allocation.
    // Templates without that entry never hold an empty value, so they are unaffected.
    if (sellingPlanEl && sellingPlanEl.value === '') return null;
    // Same rule as the linked cards: no subscription price while the frequency
    // selector is hidden, because nothing on screen would explain it.
    if (sellingPlanEl?.closest('.recharge-frequency')?.classList.contains('hidden')) return null;
    const planId = sellingPlanEl && sellingPlanEl.value ? Number(sellingPlanEl.value) : null;
    if (planId) {
      const match = variant.selling_plan_allocations.find((a) => Number(a.selling_plan_id) === planId);
      if (match) return match;
    }
    return variant.selling_plan_allocations[0];
  }

  // The variant picker carries the canonical product JSON — but main-product only
  // renders a picker for products with more than the default variant, so on a
  // single-variant product (the Water Wipes Starter Pack) there is none. The widget
  // renders its own copy in that case; prefer it, since it is scoped to this widget
  // and the picker lookup is document-wide. Returns null when neither exists, so a
  // widget on a page with no product data degrades instead of throwing.
  getProductData() {
    const dataEl =
      this.element.querySelector('script.js-plan-product-data') ||
      document.querySelector('.product-options variant-picker [type="application/json"]');
    if (!dataEl) return null;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return null;
    }

    // Filter "8 packs" or "9 packs" variant for product ID 8764080816404 based on setting
    const hide8Packs = this.dataset.hide8Packs === 'true';
    const targetProductId = 8764080816404;
    if (data.product && data.product.variants && data.product.id === targetProductId) {
      data.product.variants = data.product.variants.filter(variant => {
        const title = variant.title.toLowerCase();
        if (hide8Packs) {
          return !title.includes('8 packs');
        } else {
          return !title.includes('9 packs');
        }
      });
    }

    return data;
  }

  // The variant the widget is currently working with. Normally that is whichever
  // variant matches the picker's selected options; a single-variant product renders
  // no picker at all, so there are no options to match and the variant the page was
  // rendered for is the answer. Returns null when the product data is unavailable —
  // every caller must cope with that rather than dereference it.
  getSelectedVariant() {
    const variants = this.getProductData()?.product?.variants;
    if (!Array.isArray(variants) || !variants.length) return null;

    const selectedOptions = this.getSelectedOptions();
    if (!selectedOptions.length) {
      const currentId = this.dataset.currentVariantId;
      return variants.find((v) => String(v.id) === currentId) || variants[0];
    }

    return variants.find((v) => v.options.every((val, index) => val === selectedOptions[index])) || null;
  }

  getSelectedOptions() {
    const selectedOptions = [];

    document.querySelectorAll('.product-options variant-picker .option-selector').forEach((selector) => {
      if (selector.dataset.selectorType === 'dropdown') {
        selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
      } else {
        const checkedInput = selector.querySelector('input:checked');
        if (checkedInput) {
          selectedOptions.push(checkedInput.value);
        } else {
          // Fallback to first available option if none checked (e.g., hidden variant was pre-selected)
          const firstInput = selector.querySelector('input');
          if (firstInput) {
            firstInput.checked = true;
            selectedOptions.push(firstInput.value);
          }
        }
      }
    });

    return selectedOptions;
  }

  // An explicit badge set on the block (e.g. a discount-code message) always wins over
  // the auto-computed "Save X%", mirroring the Liquid render so nothing flashes on load.
  buildBadgeHtml(planOption, savePercentHtml) {
    const explicit = planOption.dataset.badge;
    if (!explicit) return savePercentHtml;
    const badge = document.createElement('div');
    badge.className = 'plan-option-save-badge';
    badge.textContent = explicit;
    return badge.outerHTML;
  }

  // ---------------------------------------------------------------------------
  // Money format handler
  // ---------------------------------------------------------------------------
  formatMoney(cents, format) {
    const money_format = "${{amount}}";
    if (typeof cents == 'string') { cents = cents.replace('.',''); }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = (format || money_format);

    function defaultOption(opt, def) {
      return (typeof opt == 'undefined' ? def : opt);
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal   = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) { return 0; }

      number = (number/100.0).toFixed(precision);

      var parts   = number.split('.'),
          dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
          cents   = parts[1] ? (decimal + parts[1]) : '';

      return dollars + cents;
    }

    switch(formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  };
}

customElements.define('product-flexible-plan-single', ProductFlexiblePlanSingle);